/**
 * web/src/hooks/queries/useEvents.ts
 * Events list and single-event lookup. The list is rarely-changing data —
 * 5-minute staleTime keeps the EventSwitcher snappy.
 */
import { useQuery } from '@tanstack/react-query';
import { listEvents, fetchEvent } from '../../api/events';
import { qk } from '../../lib/queryKeys';

/**
 * Fetches GET /api/events. Cached 5 minutes; refetched on window-focus
 * is disabled globally (D-2.1 — WS push handles freshness elsewhere).
 *
 * @returns React Query result for Event[]
 * @sideEffects HTTP GET
 * @idempotent yes
 */
export function useEvents() {
  return useQuery({ queryKey: qk.events.all(), queryFn: listEvents, staleTime: 5 * 60_000 });
}

/**
 * Fetches GET /api/events/:id (used by Event Management when the user lands
 * deep-linked to a specific event).
 *
 * @param id event id; query disabled while undefined
 * @returns React Query result for Event
 * @sideEffects HTTP GET
 * @idempotent yes
 */
export function useEventById(id: string | undefined) {
  return useQuery({
    queryKey: qk.events.detail(id),
    queryFn: () => fetchEvent(id!),
    enabled: id != null,
  });
}
